import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs as AntTabs } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import BaseButton from 'components/atoms/baseButton';
import { ModuleType, Roles } from 'infrastructure/enums';
import useUser from 'utils/useUser';
import EditProvider from 'infrastructure/providers/edit-provider';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import {
  isCcmModule,
  isCombinedModule,
  isRpmModule,
} from 'infrastructure/functions';

import AdminTabbedFormPatientInfo from './tabs/patient-info';
import AdminTabbedFormClinicInfo from './tabs/clinic-info';
import AdminTabbedFormOtherInfo from './tabs/other-info';
import AdminTabbedFormLogs from './tabs/logs';
import AdminTabbedFormConditions from './tabs/conditions';
import s from './styles.module.scss';
import AdminTabbedFormPatientInsurance from './tabs/patient-insurance';
import AdminTabbedFormDevicesThresholds from './tabs/devices-thresholds';
import AdminErroneousActivity from './tabs/erroneous-activity';
import BingoCardProgress from './tabs/bingo-card-progress';

type TabKey =
  | 'patientInfo'
  | 'clinicInfo'
  | 'otherInfo'
  | 'devicesAndThresholds'
  | 'conditions'
  | 'patientInsurance'
  | 'erroneousActivity'
  | 'bingoProgress'
  | 'logs';

interface IAdminPanelPatientDetailTabsProps {
  patientGuid: string;
  module: ModuleType;
  readOnly?: boolean;
}

const Tabs: React.FC<IAdminPanelPatientDetailTabsProps> = (props) => {
  const { patientGuid, module, readOnly = false } = props;
  const { t } = useTranslation();

  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const { userType, isAdmin, isPatient, roleNames } = useUser();
  const [isExpanded, setIsExpanded] = useState(true);

  const items = useMemo(() => {
    let tabs: { key: TabKey; label: string; children: React.ReactNode }[] = [
      {
        key: 'patientInfo',
        label: t('labels.patientInfo'),
        children: (
          <AdminTabbedFormPatientInfo
            patientGuid={patientGuid}
            module={module}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'clinicInfo',
        label: t('labels.clinicInfo'),
        children: (
          <AdminTabbedFormClinicInfo
            patientGuid={patientGuid}
            module={module}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'otherInfo',
        label: t('labels.otherInfo'),
        children: (
          <AdminTabbedFormOtherInfo
            patientGuid={patientGuid}
            module={module}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'devicesAndThresholds',
        label: isPatient
          ? t('labels.devices')
          : t('labels.devicesAndThresholds'),
        children: (
          <AdminTabbedFormDevicesThresholds
            patientGuid={patientGuid}
            module={module}
            rpmStatus={patientInfo?.status}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'conditions',
        label: t('labels.conditions'),
        children: (
          <AdminTabbedFormConditions
            patientGuid={patientGuid}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'patientInsurance',
        label: t('labels.patientInsurance'),
        children: (
          <AdminTabbedFormPatientInsurance
            patientGuid={patientGuid}
            module={module}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'bingoProgress',
        label: t('labels.patientBingoProgress'),
        children: (
          <BingoCardProgress
            patientGuid={patientGuid}
            module={module}
            readOnly={readOnly}
          />
        ),
      },
      {
        key: 'logs',
        label: t('labels.logs'),
        children: (
          <AdminTabbedFormLogs patientGuid={patientGuid} module={module} />
        ),
      },
      {
        key: 'erroneousActivity',
        label: t('labels.erroneousReadings'),
        children: <AdminErroneousActivity patientGuid={patientGuid} />,
      },
    ];

    if (isRpmModule(module)) {
      const excludedTabs: TabKey[] = ['conditions'];
      tabs = tabs.filter((tab) => !excludedTabs.includes(tab.key));
    }

    const erroneousAccessRoles = [Roles.Default, Roles.Clinical];
    if (
      module !== ModuleType.ADMIN ||
      !(isAdmin && roleNames.some((r) => erroneousAccessRoles.includes(r)))
    ) {
      const excludedTabs = ['erroneousActivity'];
      tabs = tabs.filter((tab) => !excludedTabs.includes(tab.key as string));
    }

    if (isCcmModule(module)) {
      const excludedTabs: TabKey[] = [
        'devicesAndThresholds',
        'conditions',
        'patientInsurance',
      ];
      tabs = tabs.filter((tab) => !excludedTabs.includes(tab.key));
    }

    if (isCombinedModule(module)) {
      const excludedTabs: TabKey[] = ['conditions'];
      tabs = tabs.filter((tab) => !excludedTabs.includes(tab.key));
    }

    if (isPatient) {
      const excludedTabs: TabKey[] = ['logs'];
      tabs = tabs.filter((tab) => !excludedTabs.includes(tab.key));
    }
    return tabs;
  }, [patientGuid, module, readOnly, patientInfo, userType]);

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <BaseButton
          type="secondary"
          className={s['toggle-button']}
          icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
          onClick={() => setIsExpanded(!isExpanded)}
        />
        <h4 className={s.title}>{t('labels.patientInformation')}</h4>
      </div>
      {isExpanded && (
        <AntTabs
          key={`${patientGuid}-${readOnly}`}
          defaultActiveKey="patientInfo"
          items={items}
          className={s.tabs}
        />
      )}
    </div>
  );
};

const AdminPanelPatientDetailTabs: React.FC<
  IAdminPanelPatientDetailTabsProps
> = (props) => {
  return (
    <EditProvider>
      <Tabs {...props} />
    </EditProvider>
  );
};
export default AdminPanelPatientDetailTabs;
